import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';

import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { ContentWrapper } from '@components';
import { ICurrency } from '@containers/App/model';
import { VoucherDiscountMaster } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount';
import { IVoucherDiscount } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/VoucherDiscount';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { IImageProperties } from '@helpers/utils';

import { MainImage } from './MainImage';
import { messages } from './messages';
import {
  Annotation,
  BuyLink,
  Description,
  DescriptionWrapper,
  H1,
  H2,
  TopSection,
  VoucherWrapper,
  WhiteBlock,
} from './styled';

interface IHeaderProps {
  annotation: string;
  productFullName: string;
  productName: string;
  redirectUrl: string;
  simpleDescription: string;
  selectedVariant: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number];
  imageAttrs: IImageProperties;
  currency: ICurrency;
}

export const Header: React.FC<IHeaderProps> = ({
  annotation,
  productFullName,
  imageAttrs,
  redirectUrl,
  simpleDescription,
  selectedVariant,
  currency,
  productName,
}) => {
  const [ref, inView] = useInView({ initialInView: true });
  const { getTimeFromInit } = useTrackingContext();

  const handleBuyLinkClicked = () => {
    dispatchTrackingEvent({
      event: 'element_click',
      element: {
        action: 'open_pdp',
        type: 'review',
        timing: getTimeFromInit(),
        name: 'open_pdp',
        interaction: inView ? 'button' : 'scrolled_button',
      },
      _clear: true,
    });
  };

  const voucherDiscount: IVoucherDiscount = selectedVariant.attributes
    .VoucherDiscount
    ? {
        ...selectedVariant.attributes.VoucherDiscount,
        displayType: 'Percentage',
      }
    : null;

  return (
    <TopSection ref={ref}>
      <MainImage imageAttrs={imageAttrs} />

      <DescriptionWrapper>
        <H1>
          <FormattedMessage
            {...messages.reviewsPageTitle}
            values={{ productFullName }}
          />
        </H1>

        <Annotation>{annotation}</Annotation>

        {simpleDescription && (
          <Description
            dangerouslySetInnerHTML={{ __html: simpleDescription }}
          />
        )}

        {voucherDiscount && (
          <VoucherWrapper>
            <VoucherDiscountMaster
              currency={currency}
              voucherDiscount={voucherDiscount}
              hideLine={true}
              isArrowHidden={true}
            />
          </VoucherWrapper>
        )}

        <BuyLink onClick={handleBuyLinkClicked} href={redirectUrl}>
          <FormattedMessage {...messages.reviewsPageBuy} />
        </BuyLink>
      </DescriptionWrapper>

      {simpleDescription && (
        <Description dangerouslySetInnerHTML={{ __html: simpleDescription }} />
      )}

      <BuyLink
        onClick={handleBuyLinkClicked}
        fullWidth={true}
        href={redirectUrl}
      >
        <FormattedMessage {...messages.reviewsPageBuy} />
      </BuyLink>

      {!inView && (
        <WhiteBlock>
          <ContentWrapper>
            <H2>{productName}</H2>

            <BuyLink
              onClick={handleBuyLinkClicked}
              href={redirectUrl}
              fullWidth={true}
            >
              <FormattedMessage {...messages.reviewsPageBuy} />
            </BuyLink>
          </ContentWrapper>
        </WhiteBlock>
      )}
    </TopSection>
  );
};
